/* Unit search matcher backed by the automation platform's inventory API.
 * Solves what Shopify search can't: partial stock numbers ("1404" finds
 * stock 140433), VIN fragments, and model codes. The full inventory
 * (~400 units) is fetched once and cached in sessionStorage for 10 minutes;
 * matching is instant and local after that.
 *
 * Results page: Shopify's own search is loose (typo tolerance, OR across
 * words, matches inside descriptions) — "DTX" returned 84 units, only 9 of
 * them MAXX-D DTX dumps (Greg 9/3). So when the matcher finds units, the
 * results page is re-queried by SKU (`variants.sku:"012493" OR ...`), which
 * Shopify resolves exactly and renders with the theme's full trailer cards,
 * pagination, and sold-last ordering. The shopper's original words ride
 * along as `orig` for display. Broad queries (>MAX_SKU hits) and misses fall
 * through to Shopify's normal search.
 *
 * Config comes from window.PBT_INVENTORY_API = { url, key } (set in theme.liquid).
 */
window.PBTSearch = (function () {
  var CACHE_KEY = 'pbt-inv-v1';
  var CACHE_MS = 10 * 60 * 1000;
  var MAX_SKU = 100;
  var SKU_PREFIX = 'variants.sku:';
  var SEARCH_PATH = '/search';
  var memo = null;
  var pending = null;

  function norm(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  function fromCache() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      if (Date.now() - obj.at > CACHE_MS) return null;
      return obj.items;
    } catch (e) { return null; }
  }

  function toCache(items) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), items: items }));
    } catch (e) { /* private mode etc. — matching still works, just refetches */ }
  }

  function load() {
    if (memo) return Promise.resolve(memo);
    var cached = fromCache();
    if (cached) { memo = index(cached); return Promise.resolve(memo); }
    if (pending) return pending;
    var cfg = window.PBT_INVENTORY_API;
    if (!cfg || !cfg.url) return Promise.resolve([]);
    pending = fetch(cfg.url + '?api_key=' + encodeURIComponent(cfg.key))
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        var items = (d && d.items) || [];
        toCache(items);
        memo = index(items);
        pending = null;
        return memo;
      })
      .catch(function () { pending = null; return []; });
    return pending;
  }

  function index(items) {
    // Only surface units that live on THIS site. New units can briefly carry
    // their Revvable source link before their first Shopify sync — never
    // send a shopper there.
    items = items.filter(function (it) {
      var u = String(it.url || '');
      return u.indexOf('poplarblufftrailer.com') !== -1 || u.charAt(0) === '/';
    });
    return items.map(function (it) {
      return {
        title: it.title || '',
        url: it.url || '',
        image: it.image || '',
        price: it.sale_price_usd || it.price_usd || null,
        // Raw identifiers: the Shopify variant SKU is the stock number (VIN
        // when a unit has none), so these drive the exact results query.
        stock: it.stock_number || '',
        vin: it.vin || '',
        // Sold units stay searchable (the team looks them up by stock #)
        // but rank behind in-stock ones and stay out of the header dropdown.
        sold: Boolean(it.availability) && it.availability !== 'in_stock',
        _stock: norm(it.stock_number),
        _vin: norm(it.vin),
        _title: norm(it.title),
        _brand: norm(it.brand),
        _type: norm(it.product_type),
      };
    });
  }

  function score(it, nq) {
    if (!nq) return 0;
    if (it._stock && it._stock === nq) return 120;
    if (it._stock && it._stock.indexOf(nq) === 0) return 100;
    if (it._stock && it._stock.indexOf(nq) !== -1) return 85;
    if (it._vin && nq.length >= 4 && it._vin.slice(-8).indexOf(nq) !== -1) return 75;
    if (it._vin && nq.length >= 6 && it._vin.indexOf(nq) !== -1) return 70;
    if (it._title.indexOf(nq) !== -1) return 55;
    return 0;
  }

  function match(q, limit) {
    limit = limit || 10;
    var nq = norm(q);
    if (nq.length < 2) return Promise.resolve([]);
    var words = String(q).toLowerCase().split(/\s+/).map(norm).filter(function (w) { return w.length >= 2; });
    return load().then(function (items) {
      var out = [];
      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        var s = score(it, nq);
        if (!s && words.length > 1) {
          var all = words.every(function (w) {
            return it._title.indexOf(w) !== -1 || it._brand.indexOf(w) !== -1 || it._type.indexOf(w) !== -1;
          });
          if (all) s = 45;
        }
        if (s && it.sold) s -= 10;
        if (s) out.push({ item: it, s: s });
      }
      out.sort(function (a, b) { return b.s - a.s; });
      // exact = typed the unit's full stock number; Enter may jump straight
      // to it. Anything weaker should go to the results page instead.
      return out.slice(0, limit).map(function (o) {
        return Object.assign({}, o.item, { exact: o.s >= 120 });
      });
    });
  }

  /** True when `q` is already one of our SKU-scoped result queries. */
  function isSkuQuery(q) {
    return String(q || '').indexOf(SKU_PREFIX) === 0;
  }

  /** Results-page URL listing exactly the matched units, or null when the
   *  query should fall through to Shopify's own search (no hits, or too
   *  broad to express as a SKU list). */
  function resultsUrl(q) {
    q = String(q || '').trim();
    if (q.length < 2 || isSkuQuery(q)) return Promise.resolve(null);
    return match(q, MAX_SKU + 1).then(function (hits) {
      if (!hits.length || hits.length > MAX_SKU) return null;
      var seen = {};
      var terms = [];
      hits.forEach(function (h) {
        var sku = String(h.stock || h.vin || '').replace(/"/g, '').trim();
        if (!sku || seen[sku]) return;
        seen[sku] = 1;
        terms.push(SKU_PREFIX + '"' + sku + '"');
      });
      if (!terms.length) return null;
      return SEARCH_PATH + '?q=' + encodeURIComponent(terms.join(' OR ')) +
        '&type=product&orig=' + encodeURIComponent(q) +
        (terms.length > 24 ? '&view=48' : '');
    });
  }

  /** Intercept a search form: go to the exact-units URL when the matcher
   *  has hits, else let Shopify search run. If the inventory hasn't loaded
   *  within a beat, submit normally rather than making the shopper wait. */
  function bindForm(form) {
    if (!form || form.__pbtBound) return;
    form.__pbtBound = true;
    var input = form.querySelector('input[name="q"]');
    if (!input) return;
    form.addEventListener('submit', function (e) {
      var q = input.value.trim();
      if (q.length < 2 || isSkuQuery(q)) return;
      e.preventDefault();
      var done = false;
      var fallback = setTimeout(function () {
        if (done) return;
        done = true;
        form.submit();
      }, 1500);
      resultsUrl(q).then(function (url) {
        if (done) return;
        done = true;
        clearTimeout(fallback);
        if (url) window.location.href = url;
        else form.submit();
      }, function () {
        if (done) return;
        done = true;
        clearTimeout(fallback);
        form.submit();
      });
    });
  }

  /** On the results page with a plain query (typed URL, shared link, or a
   *  form that submitted before this script loaded), swap to the exact
   *  units when the matcher finds them. */
  function autoRedirect() {
    if (window.location.pathname.replace(/\/$/, '') !== SEARCH_PATH) return;
    var params = new URLSearchParams(window.location.search);
    var q = (params.get('q') || '').trim();
    if (q.length < 2 || isSkuQuery(q)) return;
    resultsUrl(q).then(function (url) {
      if (url) window.location.replace(url);
    });
  }

  function init() {
    var forms = document.querySelectorAll('form[data-pbt-search-form]');
    for (var i = 0; i < forms.length; i++) bindForm(forms[i]);
    autoRedirect();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  return { match: match, load: load, resultsUrl: resultsUrl, bindForm: bindForm, isSkuQuery: isSkuQuery };
})();
